<template>
  <div class="tryon-app">
    <!-- Tela de erro - Sem parâmetros -->
    <div v-if="!isValidAccess" class="invalid-access-screen">
      <div class="invalid-access-content">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
          <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clip-rule="evenodd" />
        </svg>
        <h2>Acesso Restrito</h2>
        <p>Este provador virtual só pode ser acessado através de uma loja parceira.</p>
      </div>
    </div>

    <!-- Tela inicial - Upload de fotos -->
    <div v-if="isValidAccess && currentStep === 'upload'" class="upload-screen">
      <!-- Botão da Galeria no canto superior direito -->
      <button
        v-if="galleryImages.length > 0"
        class="gallery-icon-btn gallery-icon-fixed"
        @click="openGalleryView"
        title="Ver meus looks"
      >
        <div class="gallery-preview-animation">
          <img
            v-for="(img, idx) in galleryImages.slice(0, 3)"
            :key="idx"
            :src="img.imageBase64 || img.resultUrl"
            :class="{ active: galleryAnimationIndex === idx }"
            alt="Preview"
          />
        </div>
        <span class="gallery-count">{{ galleryImages.length }}</span>
      </button>

      <div class="instructions">
        <img v-if="logoUrl" :src="logoUrl" alt="Logo" class="logo" />
        <h2>Provador Virtual</h2>
        <p class="intro-text">A IA usa sua foto para mapear o corpo e ajustar a roupa de forma realista.</p>
      </div>

      <!-- Aviso sobre roupas -->
      <div class="clothing-warning-wrapper">
        <span class="clothing-warning">
          Use roupas justas (camiseta e bermuda/calça). Evite roupas largas ou casacos.
        </span>
      </div>

      <!-- Fotos selecionadas -->
      <div class="photos-grid">
        <div
          v-for="(photo, index) in photos"
          :key="index"
          class="photo-card"
        >
          <div class="photo-slot">
            <img v-if="photo" :src="photo.preview" :alt="photoLabels[index]" />
            <button v-if="photo" class="remove-btn" @click="removePhoto(index)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clip-rule="evenodd" />
              </svg>
            </button>
            <div v-else class="empty-slot">
              <div class="slot-hint">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                  <path d="M12 9a3.75 3.75 0 100 7.5A3.75 3.75 0 0012 9z" />
                  <path fill-rule="evenodd" d="M9.344 3.071a49.52 49.52 0 015.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 01-3 3H4.5a3 3 0 01-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.151-.178a1.56 1.56 0 001.11-.71l.822-1.315a2.942 2.942 0 012.332-1.39zM6.75 12.75a5.25 5.25 0 1110.5 0 5.25 5.25 0 01-10.5 0zm12-1.5a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
                </svg>
                <span>Sua foto de frente e com boa iluminação</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Área de ação inferior -->
      <div class="bottom-actions">
        <!-- Botões de adicionar foto (sempre visíveis se não tiver todas) -->
        <div v-if="photos.filter(p => p).length < maxPhotos" class="action-buttons">
          <button class="btn btn-camera btn-sm" @click="openCamera">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M12 9a3.75 3.75 0 100 7.5A3.75 3.75 0 0012 9z" />
              <path fill-rule="evenodd" d="M9.344 3.071a49.52 49.52 0 015.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 01-3 3H4.5a3 3 0 01-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.151-.178a1.56 1.56 0 001.11-.71l.822-1.315a2.942 2.942 0 012.332-1.39zM6.75 12.75a5.25 5.25 0 1110.5 0 5.25 5.25 0 01-10.5 0zm12-1.5a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
            </svg>
            Tirar Foto
          </button>
          <span class="action-separator">ou</span>
          <button class="btn btn-gallery btn-sm" @click="openGallery">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
            </svg>
            Enviar sua Foto
          </button>
        </div>

        <!-- Se tem pelo menos 1 foto, mostra checkbox de termos e botão experimentar -->
        <div v-if="photos.filter(p => p).length >= 1" class="action-buttons action-buttons-submit">
          <div class="submit-inline">
            <label class="terms-checkbox">
              <input type="checkbox" v-model="termsAccepted" />
              <span>Li e aceito os <a href="#" @click.prevent="showTermsModal = true">termos de uso</a></span>
            </label>
            <button
              class="btn btn-submit"
              @click="initiateSubmit"
              :disabled="isLoading || !termsAccepted"
            >
              <span v-if="isLoading">Processando...</span>
              <span v-else>Experimentar Roupa</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Input file oculto -->
      <input
        type="file"
        ref="fileInput"
        accept="image/*"
        @change="handleFileSelect"
        style="display: none"
      />
    </div>

    <!-- Tela da câmera -->
    <div v-if="isValidAccess && currentStep === 'camera'" class="camera-screen">
      <video ref="videoElement" autoplay playsinline></video>

      <!-- Flash overlay -->
      <div ref="flashOverlay" class="flash-overlay"></div>

      <!-- Top bar da câmera -->
      <div class="camera-top-bar">
        <!-- Indicador de qual foto está tirando (esquerda) -->
        <div class="camera-guide">
          <span>{{ currentPhotoLabel }}</span>
        </div>

        <!-- Dropdown de seleção de câmera (direita) -->
        <div v-if="hasMultipleCameras" class="camera-selector">
          <button class="btn btn-camera-select" @click="toggleCameraDropdown">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M12 9a3.75 3.75 0 100 7.5A3.75 3.75 0 0012 9z" />
              <path fill-rule="evenodd" d="M9.344 3.071a49.52 49.52 0 015.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 01-3 3H4.5a3 3 0 01-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.151-.178a1.56 1.56 0 001.11-.71l.822-1.315a2.942 2.942 0 012.332-1.39zM6.75 12.75a5.25 5.25 0 1110.5 0 5.25 5.25 0 01-10.5 0zm12-1.5a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
            </svg>
            <span class="camera-select-label">{{ getSelectedCameraLabel() }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16" class="dropdown-arrow">
              <path fill-rule="evenodd" d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z" clip-rule="evenodd" />
            </svg>
          </button>

          <div v-if="showCameraDropdown" class="camera-dropdown">
            <button
              v-for="camera in availableCameras"
              :key="camera.id"
              class="camera-option"
              :class="{ active: camera.id === selectedCameraId }"
              @click="selectCamera(camera.id)"
            >
              <svg v-if="camera.id === selectedCameraId" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clip-rule="evenodd" />
              </svg>
              <span>{{ camera.label }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Countdown overlay -->
      <div v-if="countdown > 0" class="countdown-overlay">
        <span class="countdown-number">{{ countdown }}</span>
      </div>

      <div class="camera-controls">
        <button class="btn btn-cancel" @click="closeCamera">Cancelar</button>
        <button class="btn btn-capture" @click="startCountdown" :disabled="countdown > 0">
          <div class="capture-circle" :class="{ 'counting': countdown > 0 }"></div>
        </button>
        <div class="camera-controls-spacer"></div>
      </div>
    </div>

    <!-- Canvas oculto para captura -->
    <canvas ref="canvasElement" style="display: none"></canvas>

    <!-- Tela da galeria (Instagram-style) -->
    <div v-if="isValidAccess && currentStep === 'gallery'" class="gallery-screen">
      <div class="gallery-header">
        <button class="btn-back-arrow" @click="closeGalleryView">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
          </svg>
        </button>
        <h2>Meus Looks</h2>
        <span class="gallery-count-header">{{ galleryImages.length }}</span>
      </div>

      <div class="gallery-grid">
        <div
          v-for="(image, index) in galleryImages"
          :key="image.id || index"
          class="gallery-item"
        >
          <img :src="image.imageBase64 || image.resultUrl" alt="Look" />
          <div class="gallery-item-overlay">
            <div class="gallery-item-actions">
              <button class="gallery-action-btn" @click="selectGalleryImage(image)" title="Ver">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                  <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clip-rule="evenodd" />
                </svg>
              </button>
              <button class="gallery-action-btn gallery-action-delete" @click.stop="confirmDeleteImage(image)" title="Excluir">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path fill-rule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
            <span class="gallery-item-date">{{ formatDate(image.timestamp) }}</span>
          </div>
        </div>
      </div>

      <div v-if="galleryImages.length === 0" class="gallery-empty">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
          <path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
        </svg>
        <p>Nenhum look salvo ainda</p>
        <span>Experimente roupas para salvar aqui</span>
      </div>
    </div>

    <!-- Tela de resultado -->
    <div v-if="isValidAccess && currentStep === 'result'" class="result-screen">
      <div class="result-header">
        <button class="btn-back-arrow" @click="goBackFromResult">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
          </svg>
        </button>
        <h2>Resultado</h2>
        <!-- Botão da Galeria no resultado -->
        <button
          v-if="galleryImages.length > 1"
          class="gallery-icon-btn gallery-icon-btn-small"
          @click="openGalleryView"
          title="Ver meus looks"
        >
          <div class="gallery-preview-animation">
            <img
              v-for="(img, idx) in galleryImages.slice(0, 3)"
              :key="idx"
              :src="img.imageBase64 || img.resultUrl"
              :class="{ active: galleryAnimationIndex === idx }"
              alt="Preview"
            />
          </div>
          <span class="gallery-count">{{ galleryImages.length }}</span>
        </button>
      </div>
      <!-- Nome do Produto em cima -->
      <div class="result-product-title">
        <span>{{ productInfo?.titulo || productName || 'Produto' }}</span>
      </div>

      <div class="result-layout">
        <!-- Container principal com imagem do resultado -->
        <div class="result-main-container">
          <div
            v-if="resultImage"
            class="image-zoom-container"
            @mousemove="handleImageZoom"
            @mouseleave="resetImageZoom"
          >
            <img
              ref="resultImg"
              :src="resultImage"
              alt="Resultado do provador virtual"
              :style="zoomStyle"
            />
          </div>
          <p v-else class="processing-text">Processando imagem...</p>

          <!-- Flatlay thumbnail no canto inferior direito -->
          <div class="product-thumbnail">
            <img
              v-if="flatlayThumbnail"
              :src="flatlayThumbnail"
              :alt="productInfo?.titulo || productName"
            />
            <img
              v-else-if="productInfo && productInfo.foto"
              :src="productInfo.foto"
              :alt="productInfo.titulo || productName"
              @error="handleProductImageError"
            />
            <div v-else class="product-photo-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" />
              </svg>
            </div>
            <img
              v-if="productInfo && productInfo.img"
              :src="productInfo.img"
              alt="Variante"
              class="variant-overlay-mini"
            />
          </div>
        </div>

        <!-- Botão Comprar abaixo da imagem -->
        <div class="result-buy-section">
          <button class="btn btn-buy-main" @click="buyProduct">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M2.25 2.25a.75.75 0 000 1.5h1.386c.17 0 .318.114.362.278l2.558 9.592a3.752 3.752 0 00-2.806 3.63c0 .414.336.75.75.75h15.75a.75.75 0 000-1.5H5.378A2.25 2.25 0 017.5 15h11.218a.75.75 0 00.674-.421 60.358 60.358 0 002.96-7.228.75.75 0 00-.525-.965A60.864 60.864 0 005.68 4.509l-.232-.867A1.875 1.875 0 003.636 2.25H2.25zM3.75 20.25a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM16.5 20.25a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z" />
            </svg>
            Comprar Agora
          </button>
        </div>

        <!-- Aviso IA -->
        <div class="ai-disclaimer">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
          </svg>
          <span>Imagem gerada por inteligência artificial. O resultado é uma aproximação e pode apresentar variações de cores, iluminação e detalhes da peça em relação ao produto real.</span>
        </div>

        <!-- Enviar por Email -->
        <div class="email-section-inline">
          <p class="email-title-inline">Enviar resultado por e-mail</p>
          <div class="email-form-inline">
            <input
              type="email"
              v-model="emailToSend"
              placeholder="Digite o e-mail"
              class="email-input-inline"
              :disabled="isSendingEmail"
            />
            <button
              class="btn btn-send-email-inline"
              @click="sendResultByEmail"
              :disabled="!emailToSend || isSendingEmail"
            >
              <svg v-if="!isSendingEmail" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
              </svg>
              <span v-if="isSendingEmail">...</span>
              <span v-else>Enviar</span>
            </button>
          </div>
          <p v-if="emailSentMessage" class="email-message-inline" :class="{ 'success': emailSentSuccess, 'error': !emailSentSuccess }">
            {{ emailSentMessage }}
          </p>
        </div>
      </div>
    </div>

    <!-- Modal de Termos de Uso -->
    <div v-if="showTermsModal" class="modal-overlay" @click.self="showTermsModal = false">
      <div class="modal-content terms-modal">
        <div class="modal-header">
          <h3>Termos de Uso do Provador Virtual</h3>
          <button class="modal-close" @click="showTermsModal = false">&times;</button>
        </div>
        <div class="modal-body terms-body">
          <div class="terms-text">
            <p>Última atualização: Fevereiro de 2025</p>

            <p>Estes Termos de Uso regulam o acesso e utilização do serviço de Provador Virtual disponibilizado pela plataforma wBuy.ai. Ao utilizar este serviço, você declara ter lido, compreendido e concordado integralmente com todos os termos e condições aqui estabelecidos. Caso não concorde com qualquer disposição destes termos, solicitamos que não utilize o serviço.</p>

            <p>1. Natureza do Serviço. O Provador Virtual é uma ferramenta tecnológica que utiliza inteligência artificial para gerar simulações visuais de como determinados produtos podem aparentar quando utilizados pelo usuário. O serviço opera de forma totalmente automatizada, sem intervenção ou supervisão humana no processo de geração das imagens. As imagens resultantes são criadas por algoritmos de aprendizado de máquina e processamento de imagens, estando sujeitas a limitações técnicas inerentes a qualquer sistema de inteligência artificial.</p>

            <p>2. Limitações e Isenção de Responsabilidade. O usuário reconhece e aceita que as imagens geradas pelo Provador Virtual são meramente ilustrativas e representam aproximações visuais que podem não corresponder com precisão à realidade. A plataforma, seus desenvolvedores, representantes, parceiros comerciais e a loja onde o serviço está disponibilizado não garantem a exatidão, precisão ou fidelidade das simulações geradas. Podem ocorrer distorções, inconsistências de cores, proporções inadequadas, erros de posicionamento, falhas no reconhecimento de características físicas e outras imperfeições técnicas. Em nenhuma hipótese a plataforma ou quaisquer partes relacionadas serão responsáveis por decisões de compra baseadas nas simulações, insatisfação com produtos adquiridos, danos diretos, indiretos, incidentais, consequenciais, especiais ou punitivos, lucros cessantes ou qualquer outro prejuízo decorrente do uso do serviço.</p>

            <p>3. Coleta e Uso de Dados. Para o funcionamento do serviço, coletamos as fotografias enviadas pelo usuário, dados de contato fornecidos voluntariamente (nome, e-mail e/ou WhatsApp), informações sobre o produto visualizado e dados técnicos de navegação. Estas informações são utilizadas para gerar a simulação virtual solicitada, entrar em contato em caso de falhas técnicas, melhorar e aperfeiçoar os algoritmos de inteligência artificial, realizar análises estatísticas e de desempenho do serviço, e para fins de marketing e comunicação institucional. Ao fornecer seus dados de contato, você autoriza expressamente o envio de comunicações relacionadas ao produto de seu interesse, incluindo informações sobre disponibilidade, alterações de preço, promoções, ofertas especiais, lançamentos de produtos similares e outras comunicações comerciais por e-mail, WhatsApp, SMS ou outros meios de comunicação. Você poderá solicitar a cessação dessas comunicações a qualquer momento através dos canais de atendimento disponibilizados.</p>

            <p>4. Licença de Uso de Imagens. Ao enviar suas fotografias e utilizar o serviço, você concede à plataforma wBuy.ai, suas empresas afiliadas, parceiros comerciais e clientes uma licença mundial, não exclusiva, transferível, sublicenciável, irrevogável e isenta de royalties para usar, reproduzir, modificar, adaptar, publicar, traduzir, criar obras derivadas, distribuir, executar e exibir as fotografias enviadas e as imagens geradas pelo serviço. Esta licença inclui, sem limitação, o direito de utilizar tais imagens em materiais de marketing, campanhas publicitárias, apresentações comerciais, estudos de caso, portfólios, redes sociais, websites, materiais impressos e digitais, treinamento de modelos de inteligência artificial e quaisquer outros fins comerciais ou institucionais, sem necessidade de aprovação prévia, notificação ou compensação adicional.</p>

            <p>5. Propriedade Intelectual. Todo o conteúdo, tecnologia, algoritmos, interfaces, design, marcas, logotipos e demais elementos que compõem o serviço de Provador Virtual são de propriedade exclusiva da wBuy.ai ou de seus licenciadores, estando protegidos pelas leis de propriedade intelectual aplicáveis. O usuário não adquire qualquer direito de propriedade sobre a tecnologia ou sobre as imagens geradas além da licença limitada de visualização pessoal.</p>

            <p>6. Conduta do Usuário. O usuário compromete-se a utilizar o serviço apenas para fins lícitos e de acordo com estes termos. É expressamente proibido enviar imagens que contenham conteúdo ilegal, ofensivo, difamatório, pornográfico ou que violem direitos de terceiros, utilizar o serviço para fins fraudulentos ou enganosos, tentar acessar, modificar ou interferir nos sistemas da plataforma, reproduzir ou distribuir a tecnologia do serviço sem autorização, e utilizar o serviço de forma que possa prejudicar seu funcionamento ou a experiência de outros usuários.</p>

            <p>7. Armazenamento e Exclusão de Dados. As fotografias e imagens processadas são armazenadas em nossos servidores pelo tempo necessário para a prestação do serviço e para os fins descritos nestes termos. A plataforma reserva-se o direito de manter cópias das imagens por tempo indeterminado para fins de melhoria dos algoritmos, backup, compliance legal ou outros fins legítimos. O usuário pode solicitar informações sobre seus dados através dos canais de contato disponibilizados, estando tal solicitação sujeita às limitações técnicas e legais aplicáveis.</p>

            <p>8. Modificações nos Termos. A plataforma reserva-se o direito de modificar estes termos a qualquer momento, sem aviso prévio. As modificações entrarão em vigor imediatamente após sua publicação. O uso continuado do serviço após quaisquer alterações constitui aceitação dos novos termos. Recomendamos a revisão periódica destes termos.</p>

            <p>9. Disponibilidade do Serviço. O serviço é disponibilizado "como está" e "conforme disponível", sem garantias de qualquer natureza, expressas ou implícitas. A plataforma não garante que o serviço será ininterrupto, livre de erros, seguro ou livre de vírus ou outros componentes prejudiciais. Reservamo-nos o direito de suspender, modificar ou descontinuar o serviço a qualquer momento, com ou sem aviso prévio, sem qualquer responsabilidade perante os usuários.</p>

            <p>10. Indenização. O usuário concorda em indenizar, defender e isentar a plataforma wBuy.ai, seus diretores, funcionários, agentes, parceiros e afiliados de quaisquer reclamações, perdas, danos, custos e despesas (incluindo honorários advocatícios) decorrentes ou relacionados ao uso do serviço pelo usuário, violação destes termos ou violação de quaisquer direitos de terceiros.</p>

            <p>11. Lei Aplicável e Foro. Estes termos serão regidos e interpretados de acordo com as leis da República Federativa do Brasil. Fica eleito o foro da comarca de São Paulo, Estado de São Paulo, como competente para dirimir quaisquer controvérsias decorrentes destes termos, com renúncia expressa a qualquer outro, por mais privilegiado que seja.</p>

            <p>12. Disposições Gerais. Se qualquer disposição destes termos for considerada inválida ou inexequível, as demais disposições permanecerão em pleno vigor e efeito. A falha da plataforma em exercer ou fazer valer qualquer direito ou disposição destes termos não constituirá renúncia a tal direito ou disposição. Estes termos constituem o acordo integral entre o usuário e a plataforma em relação ao uso do serviço de Provador Virtual.</p>

            <p>Ao clicar em "Aceitar" ou ao utilizar o serviço de Provador Virtual, você confirma que leu, compreendeu e concorda com todos os termos e condições acima estabelecidos, declarando-se ciente de que as imagens são geradas por inteligência artificial sem supervisão humana e que está de acordo com o uso de suas imagens conforme descrito neste documento.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showTermsModal = false">Fechar</button>
        </div>
      </div>
    </div>

    <!-- Modal de Dados de Contato -->
    <div v-if="showContactForm" class="modal-overlay" @click.self="showContactForm = false">
      <div class="modal-content contact-modal">
        <div class="modal-header">
          <h3>Seus Dados</h3>
          <button class="modal-close" @click="showContactForm = false">&times;</button>
        </div>
        <div class="modal-body">
          <p>Preencha seus dados para receber o resultado e ser notificado em caso de falha no processamento.</p>

          <div class="form-group">
            <label for="contact-name">Nome *</label>
            <input
              type="text"
              id="contact-name"
              v-model="contactData.name"
              placeholder="Seu nome"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="contact-whatsapp">WhatsApp</label>
            <input
              type="tel"
              id="contact-whatsapp"
              v-model="contactData.whatsapp"
              placeholder="(11) 99999-9999"
              class="form-input"
              @input="formatWhatsApp"
            />
          </div>

          <div class="form-group">
            <label for="contact-email">E-mail</label>
            <input
              type="email"
              id="contact-email"
              v-model="contactData.email"
              placeholder="seu@email.com"
              class="form-input"
            />
          </div>

          <p class="form-hint">* Preencha pelo menos o WhatsApp ou E-mail para continuar.</p>

          <p v-if="contactError" class="form-error">{{ contactError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showContactForm = false">Cancelar</button>
          <button class="btn btn-primary" @click="validateAndSubmit">Enviar Fotos</button>
        </div>
      </div>
    </div>

    <!-- Processing Screen - Animated -->
    <div v-if="isValidAccess && isLoading" class="processing-screen">
      <div class="processing-content">
        <!-- Current Image Being Processed -->
        <div class="processing-image-container">
          <img
            :src="processingCurrentImage"
            alt="Processando"
            class="processing-image"
          />
          <!-- Scanning Animation Overlay -->
          <div class="scan-overlay">
            <div class="scan-line"></div>
          </div>
          <!-- Corner Markers -->
          <div class="corner-marker top-left"></div>
          <div class="corner-marker top-right"></div>
          <div class="corner-marker bottom-left"></div>
          <div class="corner-marker bottom-right"></div>
        </div>

        <!-- Processing Status -->
        <div class="processing-status">
          <div class="processing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p class="processing-text">{{ processingStatusText }}</p>
        </div>

        <!-- Progress Indicator -->
        <div class="processing-progress">
          <div class="progress-steps">
            <div
              v-for="(step, index) in processingSteps"
              :key="index"
              class="progress-step"
              :class="{
                'completed': index < processingCurrentStep,
                'active': index === processingCurrentStep
              }"
            >
              <div class="step-dot"></div>
              <span class="step-label">{{ step.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Desenvolvido por -->
    <a v-if="isValidAccess" href="https://www.wbuy.com.br/ai" target="_blank" class="powered-by">
      Desenvolvido por <strong>wBuy.ai</strong>
    </a>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'App',
  data() {
    return {
      isValidAccess: false,
      currentStep: 'upload',
      maxPhotos: 2,
      agentId: null,
      wbuyAiId: null, // mantido para compatibilidade
      logoUrl: null,
      photos: [],
      isLoading: false,
      resultImage: null,
      flatlayThumbnail: null,
      videoStream: null,
      facingMode: 'user',
      hasMultipleCameras: false,
      availableCameras: [],
      selectedCameraId: null,
      showCameraDropdown: false,
      countdown: 0,
      countdownTimer: null,
      photoLabels: ['frente'],
      photoIcons: ['1'],
      photoDescriptions: [
        'De pé, braços levemente afastados do corpo.'
      ],
      // Termos de uso
      showTermsModal: false,
      termsAccepted: false,
      // Dados de contato do usuário
      showContactForm: false,
      contactData: {
        name: '',
        whatsapp: '',
        email: ''
      },
      contactError: '',
      // Dados do produto
      productId: null,
      productSku: null,
      productName: null,
      productImageUrl: null, // URL da imagem do produto (passada pelo widget)
      // URLs da página
      pageUrl: null,
      siteUrl: null,
      // Config do agente
      rootOwnersId: null,
      wbuy_ai_cart_url: null,
      // Slot alvo para foto
      targetSlotIndex: null,
      // Email
      emailToSend: '',
      isSendingEmail: false,
      emailSentMessage: '',
      emailSentSuccess: false,
      // Zoom da imagem
      zoomStyle: {},
      // Galeria de resultados
      galleryImages: [],
      galleryAnimationIndex: 0,
      galleryAnimationInterval: null,
      selectedGalleryImage: null,
      previousStep: null,
      // Dados do produto do resultado
      productInfo: null,
      // Processing animation
      processingImageIndex: 0,
      processingCurrentStep: 0,
      processingStatusText: 'Preparando análise...',
      processingInterval: null,
      processingSteps: [
        { label: 'Analisar', messages: ['Analisando imagem...', 'Detectando corpo...', 'Mapeando proporções...'] },
        { label: 'Processar', messages: ['Processando pose...', 'Ajustando medidas...', 'Preparando modelo...'] },
        { label: 'Aplicar', messages: ['Experimentando produto...', 'Ajustando caimento...', 'Finalizando...'] }
      ]
    };
  },
  computed: {
    currentPhotoLabel() {
      // Se temos um slot alvo específico, usa ele
      if (this.targetSlotIndex !== null) {
        return `Tire a foto: ${this.photoLabels[this.targetSlotIndex]}`;
      }
      const nextIndex = this.photos.findIndex(p => p === null);
      if (nextIndex === -1) return '';
      return `Tire a foto: ${this.photoLabels[nextIndex]}`;
    },
    processingCurrentImage() {
      // Retorna a imagem atual sendo processada
      const validPhotos = this.photos.filter(p => p !== null);
      if (validPhotos.length === 0) return '';
      const photo = validPhotos[this.processingImageIndex % validPhotos.length];
      // A foto pode ser um objeto com .preview ou uma string diretamente
      return photo?.preview || photo || '';
    }
  },
  async mounted() {
    // Obter parâmetros da URL
    const urlParams = new URLSearchParams(window.location.search);
    this.agentId = urlParams.get('agent_id');
    this.agentId = this.agentId; // mantém compatibilidade
    // Apenas 1 foto (frente)
    this.maxPhotos = 1;
    this.logoUrl = urlParams.get('logoUrl');

    // Dados do produto
    this.productId = urlParams.get('productId');
    this.productSku = urlParams.get('productSku');
    this.productName = urlParams.get('productName');
    this.productImageUrl = urlParams.get('productImageUrl');

    // URLs da página
    this.pageUrl = urlParams.get('pageUrl');
    this.siteUrl = urlParams.get('siteUrl');

    // Config do agente
    this.rootOwnersId = urlParams.get('rootOwnersId');
    this.wbuy_ai_cart_url = urlParams.get('wbuy_ai_cart_url');

    // Validar acesso - precisa ter agent_id
    if (!this.agentId) {
      this.isValidAccess = false;
      console.log('[Tryon App] Acesso negado: parâmetros obrigatórios ausentes');
      return;
    }
    this.isValidAccess = true;

    // Inicializar array de fotos
    this.photos = new Array(this.maxPhotos).fill(null);

    // Verificar se tem múltiplas câmeras
    this.checkCameras();

    // Primeiro, verificar se há um resultado salvo
    const hasResult = await this.loadResultFromDB();

    // Se não houver resultado, carregar fotos do IndexedDB (se houver)
    if (!hasResult) {
      await this.loadPhotosFromDB();
    }

    // Carregar galeria para o preview animado
    await this.loadGalleryForPreview();

    // Carregar dados de contato salvos (se houver)
    const savedContact = this.loadContactFromStorage();
    if (savedContact) {
      this.contactData = { ...this.contactData, ...savedContact };
    }

    console.log('[Tryon App] Iniciado com:', {
      agentId: this.agentId,
      rootOwnersId: this.rootOwnersId,
      maxPhotos: this.maxPhotos,
      productId: this.productId,
      productSku: this.productSku,
      productName: this.productName,
      productImageUrl: this.productImageUrl,
      pageUrl: this.pageUrl,
      siteUrl: this.siteUrl,
      wbuy_ai_cart_url: this.wbuy_ai_cart_url
    });
  },
  beforeUnmount() {
    this.stopCamera();
    this.stopGalleryAnimation();
  },
  methods: {
    async checkCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        this.availableCameras = cameras.map((cam, index) => ({
          id: cam.deviceId,
          label: cam.label || `Câmera ${index + 1}`
        }));
        this.hasMultipleCameras = cameras.length > 1;
        if (cameras.length > 0 && !this.selectedCameraId) {
          this.selectedCameraId = cameras[0].deviceId;
        }
      } catch (e) {
        console.log('Erro ao verificar câmeras:', e);
      }
    },

    // =========================================
    // IndexedDB - Persistência de fotos local
    // =========================================
    openDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('WBuyTryonDB', 2); // Versão 2 para adicionar store de resultados

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('photos')) {
            db.createObjectStore('photos', { keyPath: 'id' });
          }
          if (!db.objectStoreNames.contains('results')) {
            db.createObjectStore('results', { keyPath: 'id' });
          }
        };
      });
    },

    async savePhotosToDB() {
      try {
        const db = await this.openDB();
        const tx = db.transaction('photos', 'readwrite');
        const store = tx.objectStore('photos');

        // Salva as fotos com chave baseada no productId (ou wbuyAiId se não houver)
        const storageKey = this.productId || this.agentId || 'default';

        // Prepara os dados para salvar
        // Salva o File object diretamente (IndexedDB suporta Blob/File nativamente)
        const photosData = this.photos.map((photo, index) => {
          if (!photo) return null;
          return {
            index,
            file: photo.file || null,   // File object (persistível)
            label: this.photoLabels[index]
          };
        });

        await new Promise((resolve, reject) => {
          const request = store.put({
            id: storageKey,
            photos: photosData,
            timestamp: Date.now()
          });
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });

        db.close();
        console.log('[Tryon] Fotos salvas no IndexedDB');
      } catch (error) {
        console.error('[Tryon] Erro ao salvar fotos no IndexedDB:', error);
      }
    },

    async loadPhotosFromDB() {
      try {
        const db = await this.openDB();
        const tx = db.transaction('photos', 'readonly');
        const store = tx.objectStore('photos');

        const storageKey = this.productId || this.agentId || 'default';

        const data = await new Promise((resolve, reject) => {
          const request = store.get(storageKey);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        db.close();

        if (data && data.photos) {
          data.photos.forEach((photoData, index) => {
            if (!photoData || !photoData.file) return;
            try {
              // Recria blob URL a partir do File object salvo
              const preview = URL.createObjectURL(photoData.file);
              this.photos[index] = { file: photoData.file, preview };
            } catch (e) {
              console.warn('[Tryon] Foto ignorada no restore:', e.message);
            }
          });
          console.log('[Tryon] Fotos restauradas do IndexedDB');
        }
      } catch (error) {
        console.error('[Tryon] Erro ao carregar fotos do IndexedDB:', error);
      }
    },

    async clearPhotosFromDB() {
      try {
        const db = await this.openDB();
        const tx = db.transaction('photos', 'readwrite');
        const store = tx.objectStore('photos');

        const storageKey = this.productId || this.agentId || 'default';

        await new Promise((resolve, reject) => {
          const request = store.delete(storageKey);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });

        db.close();
        console.log('[Tryon] Fotos removidas do IndexedDB');
      } catch (error) {
        console.error('[Tryon] Erro ao limpar fotos do IndexedDB:', error);
      }
    },

    async saveResultToDB(resultUrl, metadata = {}, flatlayThumbnail = null) {
      try {
        // Gerar ID único para cada resultado (permite múltiplas imagens)
        const timestamp = Date.now();
        const uniqueId = `${this.agentId}_${this.productId || 'default'}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

        // Converter imagem para base64 se for URL (ANTES de abrir a transação)
        let imageBase64 = resultUrl;
        if (resultUrl && resultUrl.startsWith('http')) {
          try {
            const response = await fetch(resultUrl);
            const blob = await response.blob();
            imageBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (e) {
            console.warn('[Tryon] Não foi possível converter imagem para base64:', e);
          }
        }

        // Dados completos para IndexedDB
        const resultData = {
          id: uniqueId,
          imageBase64,
          resultUrl,
          flatlayThumbnail,
          wbuyAiId: this.agentId,
          productId: this.productId,
          productSku: this.productSku,
          productName: this.productName,
          pageUrl: this.pageUrl,
          siteUrl: this.siteUrl,
          metadata,
          productInfo: metadata.product_info || null,
          timestamp,
          createdAt: new Date().toISOString()
        };

        // Abrir transação DEPOIS de preparar os dados
        const db = await this.openDB();
        const tx = db.transaction('results', 'readwrite');
        const store = tx.objectStore('results');

        await new Promise((resolve, reject) => {
          const request = store.put(resultData);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });

        db.close();

        // Salvar referência no localStorage
        const localStorageRef = {
          indexDbKey: uniqueId,
          wbuyAiId: this.agentId,
          productId: this.productId,
          productSku: this.productSku,
          productName: this.productName,
          pageUrl: this.pageUrl,
          siteUrl: this.siteUrl,
          timestamp,
          createdAt: new Date().toISOString()
        };

        // Adicionar ao array de resultados (mantém histórico completo)
        const existingResults = JSON.parse(localStorage.getItem('wbuy_tryon_results') || '[]');
        existingResults.push(localStorageRef);
        localStorage.setItem('wbuy_tryon_results', JSON.stringify(existingResults));

        // Salvar também o último resultado para este produto/SKU (para restaurar após F5)
        const lastResultKey = this.getResultKey();
        localStorage.setItem(lastResultKey, uniqueId);

        console.log('[Tryon] Resultado salvo no IndexedDB (galeria)');
        console.log('[Tryon] Total de imagens na galeria:', existingResults.length);
        console.log('[Tryon] Dados salvos:', localStorageRef);
      } catch (error) {
        console.error('[Tryon] Erro ao salvar resultado no IndexedDB:', error);
      }
    },

    // Gera a chave única para o resultado baseado em agent, produto e SKU
    getResultKey() {
      const productKey = this.productSku || this.productId || 'default';
      return `wbuy_tryon_last_${this.agentId}_${productKey}`;
    },

    async loadResultFromDB() {
      try {
        // Buscar o ID do último resultado para este produto/SKU
        const lastResultKey = this.getResultKey();
        const lastResultId = localStorage.getItem(lastResultKey);

        if (!lastResultId) {
          return false;
        }

        const db = await this.openDB();
        const tx = db.transaction('results', 'readonly');
        const store = tx.objectStore('results');

        const data = await new Promise((resolve, reject) => {
          const request = store.get(lastResultId);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        db.close();

        if (data && (data.imageBase64 || data.resultUrl)) {
          // Verificar se o contexto bate (mesmo produto/SKU)
          const sameProduct = (data.productId === this.productId) || (!data.productId && !this.productId);
          const sameSku = (data.productSku === this.productSku) || (!data.productSku && !this.productSku);

          if (sameProduct && sameSku) {
            // Usa base64 se disponível, senão usa URL
            this.resultImage = data.imageBase64 || data.resultUrl;
            // Restaura flatlay thumbnail se disponível
            this.flatlayThumbnail = data.flatlayThumbnail || null;
            // Restaura product_info se disponível (verifica top-level primeiro, depois metadata)
            if (data.productInfo) {
              this.productInfo = data.productInfo;
            } else if (data.metadata && data.metadata.product_info) {
              this.productInfo = data.metadata.product_info;
            }
            this.currentStep = 'result';
            console.log('[Tryon] Último resultado restaurado do IndexedDB');
            console.log(`[Tryon] Produto: ${this.productId}, SKU: ${this.productSku}`);
            console.log(`[Tryon] Flatlay thumbnail: ${this.flatlayThumbnail ? 'restaurado' : 'não disponível'}`);
            return true;
          } else {
            // Contexto diferente, limpar referência antiga
            console.log('[Tryon] Resultado encontrado mas contexto diferente, ignorando');
            localStorage.removeItem(lastResultKey);
            return false;
          }
        }
        return false;
      } catch (error) {
        console.error('[Tryon] Erro ao carregar resultado do IndexedDB:', error);
        return false;
      }
    },

    async clearResultFromDB() {
      try {
        // Apenas remove a referência do "último resultado" para este produto
        // A imagem permanece na galeria do IndexedDB para histórico
        const lastResultKey = this.getResultKey();
        localStorage.removeItem(lastResultKey);

        console.log('[Tryon] Referência do último resultado removida (imagem mantida na galeria)');
      } catch (error) {
        console.error('[Tryon] Erro ao limpar referência do resultado:', error);
      }
    },

    // Método para obter todas as imagens da galeria (para uso futuro)
    async getGalleryImages() {
      try {
        const db = await this.openDB();
        const tx = db.transaction('results', 'readonly');
        const store = tx.objectStore('results');

        const allImages = await new Promise((resolve, reject) => {
          const request = store.getAll();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        db.close();

        // Ordenar por timestamp (mais recente primeiro)
        allImages.sort((a, b) => b.timestamp - a.timestamp);

        console.log(`[Tryon] Galeria carregada: ${allImages.length} imagens`);
        return allImages;
      } catch (error) {
        console.error('[Tryon] Erro ao carregar galeria:', error);
        return [];
      }
    },

    // Método para deletar uma imagem específica da galeria (para uso futuro)
    async deleteFromGallery(imageId) {
      try {
        const db = await this.openDB();
        const tx = db.transaction('results', 'readwrite');
        const store = tx.objectStore('results');

        await new Promise((resolve, reject) => {
          const request = store.delete(imageId);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });

        db.close();

        // Remover também do localStorage
        const existingResults = JSON.parse(localStorage.getItem('wbuy_tryon_results') || '[]');
        const filteredResults = existingResults.filter(r => r.indexDbKey !== imageId);
        localStorage.setItem('wbuy_tryon_results', JSON.stringify(filteredResults));

        console.log(`[Tryon] Imagem ${imageId} removida da galeria`);
        return true;
      } catch (error) {
        console.error('[Tryon] Erro ao deletar imagem da galeria:', error);
        return false;
      }
    },
    // =========================================

    openGallery() {
      console.log('[Tryon] openGallery chamado, fileInput ref:', !!this.$refs.fileInput);
      this.targetSlotIndex = null;
      this.$refs.fileInput.click();
    },

    openGalleryForSlot(index) {
      console.log('[Tryon] openGalleryForSlot chamado, slot:', index, 'fileInput ref:', !!this.$refs.fileInput);
      this.targetSlotIndex = index;
      this.$refs.fileInput.click();
    },

    openCameraForSlot(index) {
      this.targetSlotIndex = index;
      this.openCamera();
    },

    handleFileSelect(event) {
      console.log('[Tryon] handleFileSelect disparado, files:', event.target.files?.length);
      const file = event.target.files[0];
      if (!file) {
        console.warn('[Tryon] Nenhum arquivo selecionado');
        return;
      }
      console.log('[Tryon] Arquivo:', file.name, 'tipo:', file.type, 'tamanho:', file.size, 'bytes');

      // Usar blob URL direto — sem FileReader/canvas (evita problemas no iframe)
      const preview = URL.createObjectURL(file);
      console.log('[Tryon] Blob URL criado:', preview);

      this.addPhoto({ file, preview });

      // Reset input para permitir selecionar o mesmo arquivo
      event.target.value = '';
      console.log('[Tryon] handleFileSelect concluído, photos:', JSON.stringify(this.photos.map((p, i) => ({ slot: i, hasPhoto: !!p }))));
    },

    // Comprimir imagem: aceita File/Blob OU string data URL
    // Uso: compressImage(file) ou compressImage(dataUrl, maxSize, quality)
    compressImage(source, maxSize = 1920, quality = 0.85) {
      console.log('[Tryon] compressImage ENTRY, tipo:', typeof source, source instanceof File ? 'File' : (source instanceof Blob ? 'Blob' : 'string'));
      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Timeout: compressImage travou após 15s'));
        }, 15000);

        const drawAndExport = (imgSource) => {
          try {
            let width = imgSource.width || imgSource.naturalWidth;
            let height = imgSource.height || imgSource.naturalHeight;
            console.log('[Tryon] drawAndExport dimensões:', width, 'x', height);
            if (width > maxSize || height > maxSize) {
              const scale = Math.min(maxSize / width, maxSize / height);
              width = Math.round(width * scale);
              height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(imgSource, 0, 0, width, height);
            if (imgSource.close) imgSource.close();
            const compressed = canvas.toDataURL('image/jpeg', quality);
            console.log('[Tryon] canvas.toDataURL ok, length:', compressed?.length);
            clearTimeout(timeoutId);
            resolve(compressed);
          } catch (err) {
            clearTimeout(timeoutId);
            reject(err);
          }
        };

        // CASO 1: source é uma string (data URL) → carrega direto em Image
        if (typeof source === 'string') {
          console.log('[Tryon] Source é string, carregando Image...');
          const img = new Image();
          img.onload = () => { console.log('[Tryon] Image onload ok'); drawAndExport(img); };
          img.onerror = (e) => { clearTimeout(timeoutId); reject(new Error('Image falhou ao carregar data URL')); };
          img.src = source;
          return;
        }

        // CASO 2: source é File/Blob → tenta createImageBitmap primeiro
        if (typeof createImageBitmap !== 'undefined') {
          console.log('[Tryon] Usando createImageBitmap para File...');
          createImageBitmap(source)
            .then((bitmap) => {
              console.log('[Tryon] createImageBitmap ok:', bitmap.width, 'x', bitmap.height);
              drawAndExport(bitmap);
            })
            .catch((err) => {
              console.error('[Tryon] createImageBitmap falhou:', err.message, '— tentando FileReader...');
              // Fallback para FileReader
              const reader = new FileReader();
              reader.onload = (e) => {
                console.log('[Tryon] FileReader onload ok');
                const img = new Image();
                img.onload = () => drawAndExport(img);
                img.onerror = () => { clearTimeout(timeoutId); reject(new Error('Image falhou no fallback FileReader')); };
                img.src = e.target.result;
              };
              reader.onerror = () => { clearTimeout(timeoutId); reject(new Error('FileReader falhou')); };
              reader.readAsDataURL(source);
            });
          return;
        }

        // CASO 3: createImageBitmap indisponível → FileReader direto
        console.log('[Tryon] createImageBitmap indisponível, usando FileReader...');
        const reader = new FileReader();
        reader.onload = (e) => {
          console.log('[Tryon] FileReader onload ok');
          const img = new Image();
          img.onload = () => drawAndExport(img);
          img.onerror = () => { clearTimeout(timeoutId); reject(new Error('Image falhou no FileReader direto')); };
          img.src = e.target.result;
        };
        reader.onerror = () => { clearTimeout(timeoutId); reject(new Error('FileReader falhou')); };
        reader.readAsDataURL(source);
      });
    },


    addPhoto(photo) {
      console.log('[Tryon] addPhoto chamado, targetSlotIndex:', this.targetSlotIndex, 'photos atual:', JSON.stringify(this.photos.map(p => !!p)));
      // Se temos um slot alvo específico, usa ele
      if (this.targetSlotIndex !== null && this.photos[this.targetSlotIndex] === null) {
        this.photos[this.targetSlotIndex] = photo;
        this.targetSlotIndex = null;
        this.savePhotosToDB();
        console.log('[Tryon] Foto adicionada no slot alvo');
        return;
      }
      // Caso contrário, encontra o primeiro slot vazio
      const emptyIndex = this.photos.findIndex(p => p === null);
      console.log('[Tryon] Primeiro slot vazio encontrado:', emptyIndex);
      if (emptyIndex !== -1) {
        this.photos[emptyIndex] = photo;
      }
      this.targetSlotIndex = null;
      this.savePhotosToDB();
      console.log('[Tryon] Foto adicionada no slot', emptyIndex, '| photos após:', JSON.stringify(this.photos.map(p => !!p)));
    },

    removePhoto(index) {
      this.photos[index] = null;
      this.savePhotosToDB();
    },

    async openCamera() {
      // Se chamado diretamente (não de um slot), reseta o targetSlotIndex
      if (this.targetSlotIndex === null) {
        // Encontra o próximo slot vazio
        const nextEmpty = this.photos.findIndex(p => p === null);
        if (nextEmpty !== -1) {
          this.targetSlotIndex = nextEmpty;
        }
      }
      this.currentStep = 'camera';
      this.showCameraDropdown = false;
      await this.$nextTick();

      try {
        // Atualiza lista de câmeras (pode ter mudado)
        await this.checkCameras();

        const constraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        // Se tem câmera selecionada, usa ela
        if (this.selectedCameraId) {
          constraints.video.deviceId = { exact: this.selectedCameraId };
        } else {
          constraints.video.facingMode = this.facingMode;
        }

        this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.videoElement.srcObject = this.videoStream;

        // Atualiza lista de câmeras novamente (agora com labels)
        await this.checkCameras();
      } catch (error) {
        console.error('Erro ao acessar câmera:', error);
        alert('Não foi possível acessar a câmera. Verifique as permissões.');
        this.currentStep = 'upload';
      }
    },

    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
        this.videoStream = null;
      }
    },

    closeCamera() {
      this.stopCamera();
      this.countdown = 0;
      this.targetSlotIndex = null;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      this.currentStep = 'upload';
    },

    async switchCamera() {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      this.stopCamera();
      await this.openCamera();
    },

    toggleCameraDropdown() {
      this.showCameraDropdown = !this.showCameraDropdown;
    },

    async selectCamera(cameraId) {
      this.selectedCameraId = cameraId;
      this.showCameraDropdown = false;
      this.stopCamera();

      // Reinicia com a nova câmera
      try {
        const constraints = {
          video: {
            deviceId: { exact: cameraId },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.videoElement.srcObject = this.videoStream;
      } catch (error) {
        console.error('Erro ao trocar câmera:', error);
      }
    },

    getSelectedCameraLabel() {
      const cam = this.availableCameras.find(c => c.id === this.selectedCameraId);
      return cam ? cam.label : 'Câmera';
    },

    startCountdown() {
      if (this.countdown > 0) return;

      this.countdown = 3;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown === 0) {
          clearInterval(this.countdownTimer);
          this.capturePhoto();
        }
      }, 1000);
    },

    triggerFlash() {
      const flash = this.$refs.flashOverlay;
      if (flash) {
        flash.classList.add('flash-active');
        setTimeout(() => {
          flash.classList.remove('flash-active');
        }, 150);
      }
    },

    capturePhoto() {
      // Dispara o flash
      this.triggerFlash();

      const video = this.$refs.videoElement;
      const canvas = this.$refs.canvasElement;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Pequeno delay para o flash ser visível antes de fechar
      setTimeout(() => {
        // Redimensionar se necessário (máximo 1920px)
        let finalCanvas = canvas;
        const maxSize = 1920;
        if (canvas.width > maxSize || canvas.height > maxSize) {
          finalCanvas = document.createElement('canvas');
          const scale = Math.min(maxSize / canvas.width, maxSize / canvas.height);
          finalCanvas.width = canvas.width * scale;
          finalCanvas.height = canvas.height * scale;
          const finalCtx = finalCanvas.getContext('2d');
          finalCtx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
        }

        finalCanvas.toBlob((blob) => {
          const nextIndex = this.photos.findIndex(p => p === null);
          const label = this.photoLabels[nextIndex] || 'photo';
          const file = new File([blob], `${label}_${Date.now()}.jpg`, { type: 'image/jpeg' });
          // Comprimir preview também com qualidade 0.85 (85%)
          const preview = finalCanvas.toDataURL('image/jpeg', 0.85);

          this.addPhoto({ file, preview });
          this.closeCamera();
        }, 'image/jpeg', 0.85);
      }, 100);
    },

    async submitPhotos() {
      console.log('[Tryon] submitPhotos iniciado');
      const photosToSubmit = this.photos
        .map((photo, index) => photo ? { photo, index } : null)
        .filter(p => p !== null);

      console.log('[Tryon] fotos para enviar:', photosToSubmit.length);

      if (photosToSubmit.length === 0) {
        alert('Selecione pelo menos uma foto');
        return;
      }

      this.isLoading = true;
      this.startProcessingAnimation();

      try {
        // Converter fotos para base64 com resize para max 1024px
        console.log('[Tryon] Convertendo e redimensionando fotos (max 1024px)...');
        const photosPayload = await Promise.all(
          photosToSubmit.map(async ({ photo, index }) => {
            console.log('[Tryon] Convertendo foto', index, '| file:', !!photo.file, 'tipo:', photo.file?.type, 'tamanho:', photo.file?.size);
            const file = photo.file;
            const arrayBuffer = await file.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            const chunkSize = 8192;
            let binary = '';
            for (let i = 0; i < bytes.length; i += chunkSize) {
              binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
            }
            const rawBase64 = `data:${file.type || 'image/jpeg'};base64,${btoa(binary)}`;
            const base64 = await this.compressImage(rawBase64, 1024, 0.85);
            const sizeKB = Math.round((base64.length * 0.75) / 1024);
            console.log('[Tryon] Foto', index, 'redimensionada:', sizeKB, 'KB');
            return {
              index,
              label: this.photoLabels[index],
              data: base64
            };
          })
        );

        // Limpar número de whatsapp (apenas dígitos)
        const cleanWhatsapp = this.contactData.whatsapp ? this.contactData.whatsapp.replace(/\D/g, '') : '';

        const payload = {
          agent_id: this.agentId,
          root_owners_id: this.rootOwnersId,
          product_id: this.productId,
          product_sku: this.productSku,
          product_name: this.productName,
          product_image_url: this.productImageUrl, // URL da imagem do produto
          page_url: this.pageUrl,
          site_url: this.siteUrl,
          website_url: this.wbuy_ai_cart_url,
          photos: photosPayload,
          // Dados de contato do usuário
          contact: {
            name: this.contactData.name.trim(),
            whatsapp: cleanWhatsapp,
            email: this.contactData.email.trim()
          }
        };

        // Enviar para o backend
        // Usa variável de ambiente para determinar URL
        const isProduction = process.env.VUE_APP_PRODUCTION === 'true';
        const apiBaseUrl = isProduction
          ? process.env.VUE_APP_API_URL_PROD
          : process.env.VUE_APP_API_URL_LOCAL;

        // Log detalhado do payload
        const payloadSize = JSON.stringify(payload).length;
        const payloadSizeKB = Math.round(payloadSize / 1024);
        console.log('[Tryon] Enviando para:', apiBaseUrl, '| Production:', isProduction);
        console.log('[Tryon] Tamanho do payload:', payloadSizeKB, 'KB');
        console.log('[Tryon] Fotos no payload:', photosPayload.length);

        photosPayload.forEach((photo, idx) => {
          const photoSizeKB = Math.round((photo.data.length * 0.75) / 1024);
          console.log(`  - Foto ${idx + 1}: ${photo.label} | ~${photoSizeKB}KB`);
        });

        const response = await axios.post(`${apiBaseUrl}/api/tryon/process`, payload, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 240000 // 4 minutos
        });

        if (response.data.success) {
          this.resultImage = response.data.resultUrl;
          this.flatlayThumbnail = response.data.flatlayThumbnail || null;
          this.productInfo = response.data.product_info || null;
          console.log('[Tryon] Resultado recebido - productInfo:', this.productInfo);
          console.log('[Tryon] Flatlay thumbnail:', this.flatlayThumbnail ? 'recebido' : 'não recebido');
          this.currentStep = 'result';

          // Limpa as fotos do IndexedDB após envio bem sucedido
          await this.clearPhotosFromDB();

          // Salva o resultado no IndexedDB para persistir após F5
          await this.saveResultToDB(response.data.resultUrl, {
            ...response.data.metadata,
            product_info: response.data.product_info
          }, response.data.flatlayThumbnail);

          // Recarrega a galeria para o preview animado
          await this.loadGalleryForPreview();

          // Notificar o parent
          window.parent.postMessage({
            type: 'TRYON_RESULT',
            result: response.data
          }, '*');
        } else {
          throw new Error(response.data.message || 'Erro ao processar');
        }
      } catch (error) {
        console.error('Erro ao enviar fotos:', error);

        // Verifica se é erro de produto não encontrado
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          const statusCode = error.response.status;

          if (statusCode === 429) {
            alert('Nosso provador virtual está com muitas solicitações no momento. Por favor, aguarde alguns segundos e tente novamente.');
          } else if (errorData.error_code === 'PRODUCT_NOT_FOUND') {
            alert(errorData.message || 'Desculpe, ocorreu um problema técnico com esse produto. Por favor, contate a loja informando o código do produto para que seja atualizado e tente novamente. Agradecemos sua compreensão.');
          } else {
            alert(errorData.message || 'Erro ao processar as fotos. Tente novamente.');
          }
        } else {
          alert('Erro ao processar as fotos. Tente novamente.');
        }
      } finally {
        this.stopProcessingAnimation();
        this.isLoading = false;
      }
    },

    // =========================================
    // Processing Animation
    // =========================================
    startProcessingAnimation() {
      this.processingImageIndex = 0;
      this.processingCurrentStep = 0;
      this.processingStatusText = this.processingSteps[0].messages[0];

      let messageIndex = 0;
      let stepMessageIndex = 0;
      const validPhotos = this.photos.filter(p => p !== null);
      const totalMessages = this.processingSteps.reduce((acc, step) => acc + step.messages.length, 0);
      const messagesPerImage = Math.ceil(totalMessages / Math.max(validPhotos.length, 1));

      this.processingInterval = setInterval(() => {
        // Get current step
        const currentStep = this.processingSteps[this.processingCurrentStep];
        if (!currentStep) return;

        // Update message
        stepMessageIndex++;
        if (stepMessageIndex >= currentStep.messages.length) {
          // Move to next step
          stepMessageIndex = 0;
          this.processingCurrentStep++;
          if (this.processingCurrentStep >= this.processingSteps.length) {
            this.processingCurrentStep = this.processingSteps.length - 1;
            stepMessageIndex = currentStep.messages.length - 1;
          }
        }

        const newStep = this.processingSteps[this.processingCurrentStep];
        this.processingStatusText = newStep.messages[Math.min(stepMessageIndex, newStep.messages.length - 1)];

        // Change image periodically
        messageIndex++;
        if (messageIndex % messagesPerImage === 0 && validPhotos.length > 1) {
          this.processingImageIndex = (this.processingImageIndex + 1) % validPhotos.length;
        }
      }, 2000);
    },

    stopProcessingAnimation() {
      if (this.processingInterval) {
        clearInterval(this.processingInterval);
        this.processingInterval = null;
      }
      this.processingImageIndex = 0;
      this.processingCurrentStep = 0;
    },

    goBackFromResult() {
      // Se veio da galeria, volta para a galeria
      if (this.selectedGalleryImage) {
        this.selectedGalleryImage = null;
        this.resultImage = null;
        this.currentStep = 'gallery';
        return;
      }
      // Caso contrário, volta para upload
      this.resetToUpload();
    },

    async resetToUpload() {
      this.photos = new Array(this.maxPhotos).fill(null);
      this.resultImage = null;
      this.flatlayThumbnail = null;
      this.selectedGalleryImage = null;
      this.currentStep = 'upload';
      // Limpa as fotos e resultado do IndexedDB ao resetar
      await this.clearPhotosFromDB();
      await this.clearResultFromDB();
      // Recarrega a galeria para atualizar o preview
      await this.loadGalleryForPreview();
    },

    buyProduct() {
      // Redireciona para o carrinho com o produto
      if (this.wbuy_ai_cart_url && (this.productSku || this.productId)) {
        let checkoutUrl = this.wbuy_ai_cart_url;

        // Garante que a URL tenha o padrão /carrinho/add/
        if (!checkoutUrl.includes('/carrinho/')) {
          checkoutUrl = checkoutUrl.replace(/\/$/, '') + '/carrinho';
        }
        if (!checkoutUrl.includes('/add/')) {
          checkoutUrl += '/add/';
        }

        // Monta a URL com o SKU do produto (quantidade 1)
        checkoutUrl += '?';
        const sku = this.productSku || this.productId;
        checkoutUrl += `sku=${sku}|1&utm_source=wbuy_ai`;

        // Abre em nova aba
        window.open(checkoutUrl, '_blank');
      }

      // Notifica o parent para possíveis ações adicionais
      window.parent.postMessage({
        type: 'TRYON_BUY',
        productId: this.productId,
        productSku: this.productSku,
        productName: this.productName
      }, '*');
    },

    async sendResultByEmail() {
      if (!this.emailToSend || !this.resultImage) return;

      // Validação básica de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.emailToSend)) {
        this.emailSentMessage = 'Por favor, digite um e-mail válido.';
        this.emailSentSuccess = false;
        return;
      }

      this.isSendingEmail = true;
      this.emailSentMessage = '';

      try {
        const isProduction = process.env.VUE_APP_PRODUCTION === 'true';
        const apiBaseUrl = isProduction
          ? process.env.VUE_APP_API_URL_PROD
          : process.env.VUE_APP_API_URL_LOCAL;

        const response = await axios.post(`${apiBaseUrl}/api/tryon/send-email`, {
          email: this.emailToSend,
          resultUrl: this.resultImage,
          agent_id: this.agentId,
          root_owners_id: this.rootOwnersId,
          product_id: this.productId,
          product_sku: this.productSku,
          product_name: this.productName,
          page_url: this.pageUrl,
          site_url: this.siteUrl,
          website_url: this.wbuy_ai_cart_url,
          logo_url: this.logoUrl
        });

        if (response.data.success) {
          this.emailSentMessage = 'E-mail enviado com sucesso!';
          this.emailSentSuccess = true;
          this.emailToSend = '';
        } else {
          throw new Error(response.data.message || 'Erro ao enviar');
        }
      } catch (error) {
        console.error('Erro ao enviar e-mail:', error);
        this.emailSentMessage = 'Erro ao enviar e-mail. Tente novamente.';
        this.emailSentSuccess = false;
      } finally {
        this.isSendingEmail = false;

        // Limpa a mensagem após 5 segundos
        setTimeout(() => {
          this.emailSentMessage = '';
        }, 5000);
      }
    },

    closeWidget() {
      window.parent.postMessage({ type: 'TRYON_CLOSE' }, '*');
    },

    handleImageZoom(event) {
      const container = event.currentTarget;
      const rect = container.getBoundingClientRect();

      // Calcula a posição do mouse relativa ao container (0 a 1)
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      // Converte para porcentagem para transform-origin
      const originX = x * 100;
      const originY = y * 100;

      this.zoomStyle = {
        transform: 'scale(2)',
        transformOrigin: `${originX}% ${originY}%`
      };
    },

    resetImageZoom() {
      this.zoomStyle = {
        transform: 'scale(1)',
        transformOrigin: 'center center'
      };
    },

    // =========================================
    // Galeria de Looks (Instagram-style)
    // =========================================
    async openGalleryView() {
      this.stopGalleryAnimation();
      this.galleryImages = await this.getGalleryImages();
      this.previousStep = this.currentStep; // Salva o passo atual para voltar depois
      this.currentStep = 'gallery';
    },

    closeGalleryView() {
      this.currentStep = this.previousStep || 'upload';
      this.startGalleryAnimation();
    },

    selectGalleryImage(image) {
      // Mostra a imagem selecionada na tela de resultado
      this.selectedGalleryImage = image;
      this.resultImage = image.imageBase64 || image.resultUrl;
      this.flatlayThumbnail = image.flatlayThumbnail || null;
      // Carrega product_info se disponível (verifica top-level primeiro, depois metadata)
      if (image.productInfo) {
        this.productInfo = image.productInfo;
      } else if (image.metadata && image.metadata.product_info) {
        this.productInfo = image.metadata.product_info;
      } else {
        this.productInfo = null;
      }
      this.currentStep = 'result';
    },

    startGalleryAnimation() {
      if (this.galleryAnimationInterval) return;
      if (this.galleryImages.length < 2) return;

      this.galleryAnimationInterval = setInterval(() => {
        this.galleryAnimationIndex = (this.galleryAnimationIndex + 1) % Math.min(this.galleryImages.length, 3);
      }, 2000);
    },

    stopGalleryAnimation() {
      if (this.galleryAnimationInterval) {
        clearInterval(this.galleryAnimationInterval);
        this.galleryAnimationInterval = null;
      }
    },

    async loadGalleryForPreview() {
      this.galleryImages = await this.getGalleryImages();
      if (this.galleryImages.length > 0) {
        this.startGalleryAnimation();
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Hoje';
      } else if (diffDays === 1) {
        return 'Ontem';
      } else if (diffDays < 7) {
        return `${diffDays} dias`;
      } else {
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
      }
    },

    async confirmDeleteImage(image) {
      if (confirm('Deseja excluir esta imagem?')) {
        const deleted = await this.deleteFromGallery(image.id);
        if (deleted) {
          // Atualiza a lista de imagens
          this.galleryImages = this.galleryImages.filter(img => img.id !== image.id);
        }
      }
    },

    handleProductImageError(event) {
      console.log('[Tryon] Erro ao carregar imagem do produto:', event.target.src);
      // Esconde a imagem com erro
      event.target.style.display = 'none';
    },

    // =========================================
    // Termos de Uso e Dados de Contato
    // =========================================

    initiateSubmit() {
      console.log('[Tryon] initiateSubmit chamado, termsAccepted:', this.termsAccepted, 'isLoading:', this.isLoading);
      console.log('[Tryon] fotos válidas:', this.photos.filter(p => p).length);

      const savedContact = this.loadContactFromStorage();
      if (savedContact) {
        this.contactData = { ...this.contactData, ...savedContact };
      }

      console.log('[Tryon] Abrindo modal de contato, showContactForm antes:', this.showContactForm);
      this.showContactForm = true;
      console.log('[Tryon] showContactForm após:', this.showContactForm);
    },

    loadContactFromStorage() {
      try {
        const saved = localStorage.getItem('wbuy_tryon_contact');
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.log('[Tryon] Erro ao carregar contato do storage:', e);
      }
      return null;
    },

    saveContactToStorage() {
      try {
        localStorage.setItem('wbuy_tryon_contact', JSON.stringify(this.contactData));
      } catch (e) {
        console.log('[Tryon] Erro ao salvar contato no storage:', e);
      }
    },

    acceptTerms() {
      this.termsAccepted = true;
      this.showTermsModal = false;
      // Após aceitar os termos, mostra formulário de contato
      this.showContactForm = true;
    },

    formatWhatsApp(event) {
      let value = event.target.value.replace(/\D/g, '');
      if (value.length > 11) value = value.slice(0, 11);

      if (value.length > 6) {
        value = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
      } else if (value.length > 2) {
        value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
      } else if (value.length > 0) {
        value = `(${value}`;
      }

      this.contactData.whatsapp = value;
    },

    validateAndSubmit() {
      console.log('[Tryon] validateAndSubmit chamado, contactData:', JSON.stringify(this.contactData));
      this.contactError = '';

      if (!this.contactData.name || this.contactData.name.trim().length < 2) {
        this.contactError = 'Por favor, informe seu nome.';
        console.log('[Tryon] Validação falhou: nome inválido');
        return;
      }

      const hasWhatsapp = this.contactData.whatsapp && this.contactData.whatsapp.replace(/\D/g, '').length >= 10;
      const hasEmail = this.contactData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.contactData.email);

      if (!hasWhatsapp && !hasEmail) {
        this.contactError = 'Por favor, informe pelo menos o WhatsApp ou E-mail.';
        console.log('[Tryon] Validação falhou: sem whatsapp nem email');
        return;
      }

      console.log('[Tryon] Validação ok, chamando submitPhotos...');
      this.saveContactToStorage();
      this.showContactForm = false;
      this.submitPhotos();
    },

    // =========================================
    // Compressão e Redimensionamento de Imagem
    // =========================================

    async compressImage(base64Data, maxWidth = 1024, quality = 0.7) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensiona se qualquer dimensão excede o máximo
          if (width > maxWidth || height > maxWidth) {
            const scale = Math.min(maxWidth / width, maxWidth / height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converte para JPEG com a qualidade especificada
          const compressed = canvas.toDataURL('image/jpeg', quality);
          resolve(compressed);
        };
        img.src = base64Data;
      });
    }
  }
};
</script>

<style>
@import './styles/index.css';
</style>
